// ============================================================================
// WORK ITEM SEQUENCE — WORKFLOW SCOPE RESOLUTION
// ----------------------------------------------------------------------------
// `work_item_number_sequences` rows are scoped by `workflow_context`:
//   * 'default'  — the legacy client-wide row; owns numbering (prefix/counter)
//                  and is the fallback for every config lookup.
//   * <workflow> — a workflow key ('visitor-management', 'facility-management',
//                  'service-operations', …) carrying config overrides only.
//
// Resolution rule everywhere: scoped row → 'default' row → built-in defaults.
// ============================================================================

export const DEFAULT_WORKFLOW_CONTEXT = 'default';

export interface ScopedSequenceRowLike {
  item_type: string;
  workflow_context?: string | null;
  config?: Record<string, unknown> | null;
}

/** Normalize an incoming workflow/module context to a sequence scope key. */
export function normalizeWorkflowContext(context?: string | null): string {
  const trimmed = (context || '').trim();
  return trimmed.length > 0 ? trimmed : DEFAULT_WORKFLOW_CONTEXT;
}

/**
 * Pick the best-matching sequence row for an item type:
 * the row scoped to `context` if present, else the 'default' row.
 * Rows without a workflow_context (pre-migration data) count as 'default'.
 */
export function pickSequenceRow<T extends ScopedSequenceRowLike>(
  rows: T[] | undefined | null,
  itemType: string,
  context?: string | null,
): T | undefined {
  if (!rows || rows.length === 0) return undefined;
  const scope = normalizeWorkflowContext(context);
  const candidates = rows.filter((r) => r.item_type === itemType);
  if (candidates.length === 0) return undefined;
  if (scope !== DEFAULT_WORKFLOW_CONTEXT) {
    const scoped = candidates.find(
      (r) => (r.workflow_context || DEFAULT_WORKFLOW_CONTEXT) === scope,
    );
    if (scoped) return scoped;
  }
  return candidates.find(
    (r) => (r.workflow_context || DEFAULT_WORKFLOW_CONTEXT) === DEFAULT_WORKFLOW_CONTEXT,
  );
}

/**
 * Merged config for an item type in a workflow context — scoped config
 * keys override the default row's keys, per top-level config key.
 */
export function resolveSequenceConfig(
  rows: ScopedSequenceRowLike[] | undefined | null,
  itemType: string,
  context?: string | null,
): Record<string, unknown> | undefined {
  if (!rows || rows.length === 0) return undefined;
  const scope = normalizeWorkflowContext(context);
  const defaultRow = pickSequenceRow(rows, itemType, DEFAULT_WORKFLOW_CONTEXT);
  const scopedRow =
    scope === DEFAULT_WORKFLOW_CONTEXT ? undefined : pickSequenceRow(rows, itemType, scope);
  const merged = { ...(defaultRow?.config || {}), ...(scopedRow?.config || {}) };
  return Object.keys(merged).length > 0 ? merged : undefined;
}
